import type { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import {
  type BaseQueryApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";

import { auth } from "@/common/platform/firebase";
import { backendAddress } from "@/common/utilities/env";
import type { RequestFailure } from "@/state/api/requestFailure";

/**
 * One base query for every RTK Query API, with the app's behaviour injected
 * (GEN5-5269 ARCHITECTURE A2, item 3). What differs between apps is a
 * {@link BaseQueryPolicy}, given to the store once (`createAppStore({ extra:
 * { baseQueryPolicy } })`) and read here from the thunk's `extra`: one API
 * instance, each app's behaviour, and a test can pass a spy.
 */

/** Whether a failed request tells the user (A2, item 14); an endpoint opts in. */
export type NotifyOnError = "always" | "unless403" | "never";

/** What an endpoint may set in its `extraOptions`. */
export type BaseQueryExtraOptions = { notify?: NotifyOnError };

export type GenFiveBaseQueryResult = QueryReturnValue<
  unknown,
  FetchBaseQueryError,
  FetchBaseQueryMeta
>;

/**
 * The base query every endpoint is typed against. Mirrors what
 * `fetchBaseQuery` accepts (`string | FetchArgs`) so endpoints stay typed.
 */
export type GenFiveBaseQuery = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: BaseQueryExtraOptions
) => Promise<GenFiveBaseQueryResult>;

/** The app's behaviour around every request. */
export type BaseQueryPolicy = {
  /** Layout: the permissions a response states go into its auth slice. Admin: none. */
  onResult?: (
    result: GenFiveBaseQueryResult,
    url: string,
    api: BaseQueryApi
  ) => void;
  /** Tells the user about a failure, in the app's verbosity (AD1, D3), for endpoints that opt in. */
  onError?: (failure: RequestFailure, api: BaseQueryApi) => void;
  /**
   * What an endpoint without `extraOptions.notify` does on failure, in this
   * app: admin tells (`always`, AD1), layout does not (`never`, the default).
   */
  defaultNotify?: NotifyOnError;
};

/** What a store built by `createAppStore` carries in its thunk `extra`. */
export type StoreExtra = { baseQueryPolicy: BaseQueryPolicy };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNotifyOnError = (value: unknown): value is NotifyOnError =>
  value === "always" || value === "unless403" || value === "never";

const isPolicy = (value: unknown): value is BaseQueryPolicy =>
  isRecord(value) &&
  (value["onResult"] === undefined ||
    typeof value["onResult"] === "function") &&
  (value["onError"] === undefined || typeof value["onError"] === "function") &&
  (value["defaultNotify"] === undefined ||
    isNotifyOnError(value["defaultNotify"]));

/** The policy the store was built with; null for a store built without one. */
export const policyOf = (extra: unknown): BaseQueryPolicy | null =>
  isRecord(extra) && isPolicy(extra["baseQueryPolicy"])
    ? extra["baseQueryPolicy"]
    : null;

/** HTTP status of a failed request; null when the server never answered (network, CORS, timeout). */
export const getHttpStatus = (error: FetchBaseQueryError): number | null => {
  if (typeof error.status === "number") {
    return error.status;
  }
  if (error.status === "PARSING_ERROR") {
    return error.originalStatus;
  }
  return null;
};

const backendMessage = (data: unknown): string | undefined =>
  isRecord(data) && typeof data["message"] === "string"
    ? data["message"]
    : undefined;

const urlOf = (args: string | FetchArgs): string =>
  typeof args === "string" ? args : args.url;

/** A failed request as the notifier sees it. */
export const toRequestFailure = (
  args: string | FetchArgs,
  error: FetchBaseQueryError
): RequestFailure => ({
  status: getHttpStatus(error),
  statusText: typeof error.status === "string" ? error.status : undefined,
  method: typeof args === "string" ? "GET" : (args.method ?? "GET"),
  url: urlOf(args),
  message: backendMessage(error.data),
});

/**
 * Whether a failure is the user's to hear about. 404s are handled by the UI
 * where they happen and 401s roll into the sign-in flow, so neither toasts;
 * an endpoint whose 403 means "no access" (a page of its own) says
 * `unless403`. An aborted request is not a failure.
 */
const shouldNotify = (
  notify: NotifyOnError,
  failure: RequestFailure,
  api: BaseQueryApi
): boolean => {
  if (notify === "never" || api.signal.aborted) return false;
  const { status } = failure;
  if (status === null) return true;
  if (status < 400 || status === 404 || status === 401) return false;
  return !(status === 403 && notify === "unless403");
};

export type CreateBaseQueryOptions = {
  /** The policy for a store built without one (a test's ad hoc store). */
  fallbackPolicy: BaseQueryPolicy;
  /** How `params` become a query string; fetchBaseQuery's URLSearchParams when unset. */
  paramsSerializer?: (params: Record<string, unknown>) => string;
  /** Points a request at a service on this machine (local development). */
  rewriteArgs?: (args: string | FetchArgs) => string | FetchArgs;
  /** The Authorization header's scheme name, as each API has always sent it. */
  authorizationHeader?: "Authorization" | "authorization";
};

export const createBaseQuery = ({
  fallbackPolicy,
  paramsSerializer,
  rewriteArgs = (args) => args,
  authorizationHeader = "Authorization",
}: CreateBaseQueryOptions): GenFiveBaseQuery => {
  const fetchQuery = fetchBaseQuery({
    baseUrl: backendAddress(),
    prepareHeaders: async (headers) => {
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        headers.set(authorizationHeader, `Bearer ${token}`);
      }
      return headers;
    },
    ...(paramsSerializer === undefined ? {} : { paramsSerializer }),
  });

  return async (args, api, extraOptions) => {
    const policy = policyOf(api.extra) ?? fallbackPolicy;
    const result = await fetchQuery(rewriteArgs(args), api, extraOptions);
    if (result.error === undefined) {
      policy.onResult?.(result, urlOf(args), api);
    } else {
      const failure = toRequestFailure(args, result.error);
      const notify = extraOptions?.notify ?? policy.defaultNotify ?? "never";
      if (shouldNotify(notify, failure, api)) {
        policy.onError?.(failure, api);
      }
    }
    return result;
  };
};
